// Common Service Functions
import { buildURL } from './commons/CommonService.Functions';
import authFetch from './httpClient';

// Models
import { ServiceCategory, NewServiceCategory } from '../models/Entities/ServiceCategory';
import { IResult } from '../models/HttpResult';
import * as Sentry from '@sentry/react';

// Base URL
const BaseURL = process.env.REACT_APP_BILLER_API_GATEWAY + 'protocols/service-categories';

const request = async <T>(url: string, options: RequestInit, context: string): Promise<T> => {
  try {
    const response = await authFetch(url, {
      ...options,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(options.headers as Record<string, string> | undefined)
      }
    });

    if (!response.ok) {
      Sentry.captureException(response);
      throw response;
    }

    const data: IResult<T> = await response.json();
    return data.data;
  } catch (error) {
    Sentry.captureException(error);
    throw error;
  }
};

/**
 * Get all service categories
 * @returns Promise with array of service categories
 */
export const GetAllServiceCategories = async (): Promise<ServiceCategory[]> => {
  return request<ServiceCategory[]>(BaseURL, { method: 'GET' }, 'GetAllServiceCategories');
};

/**
 * Get service category by id
 * @param id - Service category id
 * @returns Promise with service category data
 */
export const GetServiceCategoryById = async (
  id: string
): Promise<ServiceCategory> => {
  return request<ServiceCategory>(buildURL(BaseURL, id), { method: 'GET' }, 'GetServiceCategoryById');
};

/**
 * Create a new service category
 * @param serviceCategory - New service category data
 * @returns Promise with created service category
 */
export const CreateServiceCategory = async (
  serviceCategory: NewServiceCategory
): Promise<ServiceCategory> => {
  return request<ServiceCategory>(
    BaseURL,
    {
      method: 'POST',
      body: JSON.stringify(serviceCategory)
    },
    'CreateServiceCategory'
  );
};

/**
 * Update an existing service category
 * @param id - Service category id
 * @param serviceCategory - Updated service category data
 * @returns Promise with updated service category
 */
export const UpdateServiceCategory = async (
  id: string,
  serviceCategory: NewServiceCategory
): Promise<ServiceCategory> => {
  return request<ServiceCategory>(
    buildURL(BaseURL, id),
    {
      method: 'PUT',
      body: JSON.stringify(serviceCategory)
    },
    'UpdateServiceCategory'
  );
};

/**
 * Delete a service category
 * @param id - Service category id
 * @returns Promise with deleted service category id
 */
export const DeleteServiceCategory = async (
  id: string
): Promise<string> => {
  return request<string>(buildURL(BaseURL, id), { method: 'DELETE' }, 'DeleteServiceCategory');
};
